<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { n } from "../../vue-cart/helpers/currency";
import { useDtfStore } from "../store";
import type { Standard } from "../types";

const props = defineProps<{
  index: number;
}>();

const store = useDtfStore();
const { t } = useI18n();

const artworks = computed(() => store.artworks);
const summary = computed(() => store.summary);
const simulation = computed(() => store.simulation);
const isPricingLoading = computed(() => store.isPricingLoading);

const artwork = computed(() => artworks.value[props.index]);

const isArtworkUploaded = computed(() => {
  const artworkId = parseInt(artwork.value?.id.toString() ?? "", 10);
  if (Number.isNaN(artworkId)) {
    return false;
  }

  return typeof artworkId === "number" && !artwork.value?.isLoading;
});

const showPricing = computed(() => props.index === 0 || isArtworkUploaded.value);

const sizesAndQuantities = computed<Standard.SizesAndQuantity[]>(
  () => artwork.value?.sizesAndQuantities ?? []
);

const sizesCount = computed(() => sizesAndQuantities.value.length);

const quantity = computed(() =>
  sizesAndQuantities.value.reduce((acc, curr) => acc + Number(curr.quantity), 0)
);

const pricing = computed(() => {
  if (artwork.value?.isFile || !sizesAndQuantities.value.some(sq => Number(sq.quantity) > 0)) {
    return simulation.value;
  }

  return sizesAndQuantities.value || simulation.value;
});

const enableAbAiMonetization = computed(() => store.enableAbAiMonetization);

function getPrice(propertyName: "retailPrice" | "unitPrice") {
  if (Array.isArray(pricing.value)) {
    return pricing.value
      .reduce((sum, item) => {
        const price = Number(item[propertyName] || 0);
        return sum + price * item.quantity;
      }, 0)
      .toFixed(2);
  }

  const price: string = (pricing.value as any)[propertyName];

  if (!price) {
    return null;
  }

  return Number(Number(price).toFixed(2)) * pricing.value.quantity;
}

const retailTotal = computed(() => getPrice("retailPrice"));

const total = computed(() => getPrice("unitPrice"));

const discount = computed(() => {
  const retail = parseFloat(retailTotal.value?.toString() ?? "");
  const actual = parseFloat(total.value?.toString() ?? "");
  if (!retail || !actual) return 0;
  return Number(Math.ceil(((retail - actual) / retail) * 100).toFixed(0));
});

const enhancements = computed(() => {
  if (!summary.value.enhancements) {
    return [];
  }

  return Object.values(summary.value.enhancements);
});

const orderSubtotal = computed(() => Number(summary.value.orderSubtotal));
const artworksReady = computed(
  () =>
    artwork.value?.isSimulated || (!artwork.value?.isLoading && artwork.value?.enhancementCompleted)
);
</script>

<template>
  <div
    v-if="pricing"
    class="multi-size-total-summary"
    :class="{
      'multi-size-total-summary--loading':
        !enableAbAiMonetization && (isPricingLoading || total === 0)
    }"
  >
    <h3 class="multi-size-total-summary__caption">Total</h3>

    <div
      v-if="enableAbAiMonetization && (isPricingLoading || !artworksReady || total === 0)"
      class="multi-size-total-summary__skeleton"
    >
      <div class="multi-size-total-summary__skeleton-block"></div>
      <div class="multi-size-total-summary__skeleton-block"></div>
      <div class="multi-size-total-summary__skeleton-block"></div>
    </div>
    <template v-else>
      <div class="multi-size-total-summary__grid">
        <template v-if="sizesCount > 0">
          <div class="multi-size-total-summary__item">
            <span class="multi-size-total-summary__label">
              <span class="multi-size-total-summary__info">for</span>
              {{ t("transfer", quantity) }}
            </span>
          </div>
          <div v-if="total && showPricing" class="multi-size-total-summary__item">
            <span class="multi-size-total-summary__price">{{ n(total, "currency") }}</span>
            <span v-if="retailTotal && discount > 0" class="multi-size-total-summary__old-price">
              {{ n(retailTotal, "currency") }}
            </span>
            <span v-if="discount > 0" class="multi-size-total-summary__discount">
              {{ discount }}% off
            </span>
          </div>
        </template>

        <template v-for="enhancement in enhancements" :key="enhancement.serviceName">
          <template v-if="Number(enhancement.totalPrice) > 0">
            <div class="multi-size-total-summary__item">
              <span class="multi-size-total-summary__label">
                <span class="multi-size-total-summary__info">for</span>
                {{ enhancement.quantity }} {{ enhancement.serviceName }}
              </span>
            </div>
            <div class="multi-size-total-summary__item">
              <span class="multi-size-total-summary__price">{{
                n(enhancement.totalPrice, "currency")
              }}</span>
            </div>
          </template>
        </template>

        <template v-if="orderSubtotal">
          <div class="multi-size-total-summary__item">
            <span class="multi-size-total-summary__label">Subtotal</span>
          </div>
          <div class="multi-size-total-summary__item">
            <span class="multi-size-total-summary__price">{{ n(orderSubtotal, "currency") }}</span>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.multi-size-total-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 13px;
    width: 280px;
  }

  &__skeleton-block {
    @include vlc-animation;

    position: relative;
    width: 100%;
    height: 18px;
    border-radius: 4px;
    background-color: $section-grey-border;
  }

  &__caption {
    margin: 0;
    color: $text-black;
    font-size: 17px;
    font-weight: 700;
    line-height: normal;
  }

  &__grid {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 60px;
    row-gap: 10px;
    width: fit-content;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__label {
    color: $text-black;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  }

  &__info {
    display: none;

    @include with-tablet {
      display: inline;
    }
  }

  &__price {
    color: $text-black;
    text-align: right;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
  }

  &__old-price {
    margin-top: auto;
    margin-left: 4px;
    color: $warm-grey;
    text-align: right;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    text-decoration-line: line-through;
  }

  &__discount {
    margin-left: 16px;
    color: $feedback-success-darker;
    text-align: right;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
  }
}
</style>
